import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { buildWhatsappUrl, track } from "@/lib/api";
import InquiryDialog from "@/components/InquiryDialog";
import {
  Home, MapPin, Package, Sparkles, Menu as MenuIcon, Phone, MessageCircle,
  X, User, Car, GalleryHorizontal, MessageSquareQuote, HelpCircle, Mail, ShieldCheck, FileText, CalendarCheck,
} from "lucide-react";

const LINK_ITEMS = [
  { to: "/", label: "Home", icon: Home, testid: "mbn-home" },
  { to: "/transportation", label: "Vehicles", icon: Car, testid: "mbn-vehicles" },
];
const LINK_ITEMS_AFTER = [
  { to: "/services", label: "Services", icon: Sparkles, testid: "mbn-services" },
];

export default function MobileBottomNav() {
  const { settings } = useSettings();
  const [openMore, setOpenMore] = useState(false);

  const phone = settings?.phone_primary;
  const wa = settings?.whatsapp_number || "+919866933180";
  const waUrl = buildWhatsappUrl(wa, "Hello! I'd like to know more about your travel packages.");

  return (
    <>
      {/* Bottom spacer so page content isn't hidden behind the bar */}
      <div className="lg:hidden h-20" aria-hidden />

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5 items-stretch">
          {LINK_ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium ${isActive ? "text-amber-600" : "text-slate-600"}`
              }
            >
              <it.icon className="w-5 h-5" />
              {it.label}
            </NavLink>
          ))}

          {/* Booking — normal item, aligned with others (no floating FAB) */}
          <InquiryDialog defaultMessage="Hello! I'd like to book a journey." source="mbn_booking" triggerTestId="mbn-booking">
            <button
              data-testid="mbn-booking"
              className="flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-semibold text-amber-700"
            >
              <CalendarCheck className="w-5 h-5" />
              Booking
            </button>
          </InquiryDialog>

          {LINK_ITEMS_AFTER.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium ${isActive ? "text-amber-600" : "text-slate-600"}`
              }
            >
              <it.icon className="w-5 h-5" />
              {it.label}
            </NavLink>
          ))}

          <button
            onClick={() => setOpenMore(true)}
            data-testid="mbn-more"
            className="flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[10px] font-medium text-slate-600"
          >
            <MenuIcon className="w-5 h-5" />
            More
          </button>
        </div>
      </nav>

      {/* More Drawer */}
      {openMore && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mbn-drawer" onClick={() => setOpenMore(false)}>
          <div className="absolute inset-0 bg-slate-950/60" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex justify-between items-center px-5 pt-4 pb-2">
              <div className="flex items-center gap-3">
                {settings?.logo_url && (
                  <img src={settings.logo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                )}
                <div className="font-display text-lg text-slate-950">{settings?.company_name || "Vizag Jagan Tours & Travels"}</div>
              </div>
              <button onClick={() => setOpenMore(false)} className="p-2" data-testid="mbn-more-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-2 pt-2 pb-6 grid grid-cols-2 gap-2">
              {[
                { to: "/about", label: "About Us", icon: User },
                { to: "/services", label: "Services", icon: Sparkles },
                { to: "/transportation", label: "Our Vehicles", icon: Car },
                { to: "/gallery", label: "Travel Gallery", icon: GalleryHorizontal },
                { to: "/testimonials", label: "Testimonials", icon: MessageSquareQuote },
                { to: "/faqs", label: "FAQ", icon: HelpCircle },
                { to: "/contact", label: "Contact Us", icon: Mail },
                { to: "/privacy", label: "Privacy Policy", icon: ShieldCheck },
                { to: "/terms", label: "Terms & Conditions", icon: FileText },
              ].map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={() => setOpenMore(false)}
                  data-testid={`mbn-more-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-slate-800 hover:bg-slate-50"
                >
                  <r.icon className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium">{r.label}</span>
                </Link>
              ))}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  onClick={() => { track("call_click", "mbn-more"); setOpenMore(false); }}
                  data-testid="mbn-more-call"
                  className="flex items-center gap-3 rounded-xl border border-brand-navy-800 bg-brand-navy-900 text-white px-3 py-3"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm font-medium">Call Us</span>
                </a>
              )}
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => { track("whatsapp_click", "mbn-more"); setOpenMore(false); }}
                data-testid="mbn-more-whatsapp"
                className="flex items-center gap-3 rounded-xl bg-[#25D366] text-white px-3 py-3"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
